// @givmo/app-admin — employer auth-policy + MFA-compliance decode (AS-WEB4 / AS-WEB5).
//
// ══ WHY EVERYTHING HERE IS HAND-DECODED OFF `unknown` ══════════════════════════════════════════
// AS-BE10 added `mfa_policy` / `mfa_grace_days` to `CompanyDetailedOut`, and AS-BE11 added
// `mfa_status` to the `GET /admin/company/employees` rows — both AFTER the committed OpenAPI
// snapshot was last refreshed, so neither is on the generated type. `agent_docs/api-client.md`
// § *Additive RESPONSE fields the snapshot lacks* is the sanctioned disposition: read them off the
// body as `unknown` and coerce defensively. Deliberately NOT "refresh the snapshot for this
// feature" — a full refresh absorbs every unrelated backend change since the last regen.
//
// ══ THE ONE THING THIS SCREEN CANNOT KNOW ═════════════════════════════════════════════════════
// ⚠ Enforcement is clamped server-side by `EMPLOYER_AUTH_POLICY_MAX_TIER` (default `encourage`),
// and that ceiling is NOT on the admin wire. So the effective tier is `min(stored, ceiling)` and a
// company can hold `require` while nothing is being enforced. The stored tier therefore says what
// the employer ASKED FOR; it never says what is happening.
//
// The roster's statuses are the honest witness, because the backend computes them from the
// EFFECTIVE tier (`employer_policy/policy.py` `_evaluate_against`): below `require` every employee
// collapses to `not_required`. Two consequences this module encodes so both surfaces agree by
// construction:
//
//   1. `enforcementObserved` — any status other than `not_required` proves the effective tier IS
//      `require`, whatever is stored. An all-`not_required` roster proves it is not. That is the
//      only enforcement claim available to a client, and it is derived from data rather than
//      assumed from the setting.
//   2. THERE IS NO ENROLLMENT PERCENTAGE BELOW `require`. `not_required` says the EMPLOYER asks
//      nothing; it says nothing about whether that person has a second factor. Reporting "0%
//      enrolled" off a roster of `not_required` would invent a fact about every employee's account
//      from a fact about the policy. `enrolledShare` is `null` in that state on purpose.

/**
 * The employer's MFA requirement — tiers L0–L2, exactly the closed enum the backend accepts
 * (`CompanyMfaPolicy`). The SSO tiers (L3/L4) are a separate `sso_mode` column landing in P4 and are
 * deliberately NOT spellable here: the PUT is `extra="forbid"` and refuses a tier it cannot enforce
 * with a 422, so a UI that could name one would be building a request the backend exists to reject.
 */
import { MFA_STATUS_LABELS, MFA_STATUS_LABELS_UNDER_REQUIREMENT } from './security-copy';

export type MfaPolicyTier = 'none' | 'encourage' | 'require';

/** The tiers in ladder order. The selector renders exactly these, so an unknown tier is unreachable. */
export const MFA_POLICY_TIERS: readonly MfaPolicyTier[] = ['none', 'encourage', 'require'];

/**
 * The backend's own defaults (`company_entities.py` `effective_mfa_policy()` /
 * `effective_mfa_grace_days()`), matched deliberately rather than defaulted to a neutral-looking
 * zero: a company row written before the AS-BE10 migration serves `null` for both, and the value the
 * BACKEND would use for it is the only one that makes this screen and the server agree.
 */
export const DEFAULT_MFA_POLICY: MfaPolicyTier = 'none';
export const DEFAULT_MFA_GRACE_DAYS = 14;

/** `MIN`/`MAX_MFA_GRACE_DAYS` — the endpoint's validated range (`ge=0`, `le=365`). 0 is legitimate:
 *  it is how an employer says "effective immediately". */
export const MIN_MFA_GRACE_DAYS = 0;
export const MAX_MFA_GRACE_DAYS = 365;

/** The windows the picker offers (plan §1.2). The endpoint accepts the wider range above, so a
 *  value already stored outside this set is preserved and shown rather than silently snapped. */
export const GRACE_DAY_PRESETS: readonly number[] = [7, 14, 30];

/** What the company has STORED. Never "what is being enforced" — see the file header. */
export interface AuthPolicyState {
  tier: MfaPolicyTier;
  graceDays: number;
}

function isTier(value: unknown): value is MfaPolicyTier {
  return (MFA_POLICY_TIERS as readonly unknown[]).includes(value);
}

/**
 * Read the stored policy off a `GET /admin/company` body.
 *
 * Absent/null/unrecognised → the backend's defaults, because that is what the backend itself would
 * use for the same row. An unknown tier string is NOT preserved: this screen submits a whole-state
 * PUT against a closed enum, so carrying a value it cannot render would mean either sending back a
 * tier the user never saw or 422-ing on save.
 */
export function readAuthPolicy(company: unknown): AuthPolicyState {
  return {
    tier: storedTier(company) ?? DEFAULT_MFA_POLICY,
    graceDays: storedGraceDays(company) ?? DEFAULT_MFA_GRACE_DAYS,
  };
}

/** The tier a body actually CARRIES, or `null` when it carries none this build recognises. */
function storedTier(source: unknown): MfaPolicyTier | null {
  const value = ((source ?? {}) as Record<string, unknown>).mfa_policy;
  return isTier(value) ? value : null;
}

/** The grace window a body actually CARRIES, clamped, or `null` when it carries none. */
function storedGraceDays(source: unknown): number | null {
  const value = ((source ?? {}) as Record<string, unknown>).mfa_grace_days;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return clampGraceDays(Math.floor(value));
}

/**
 * The policy the editor opens on, given both readings of it.
 *
 * `GET /admin/company` WINS whenever it carries the fields, and the dark-gate probe's body is the
 * fallback. That order is not arbitrary: the company object is the one the settings page owns and
 * the one a save writes back into (the PUT echoes a fresh `CompanyDetailedOut`), so preferring the
 * probe — which is fetched once per page load and never again — would show a stale tier the moment
 * an Owner saved.
 *
 * The fallback is what makes the probe worth reading at all: a company row written before the
 * AS-BE10 migration serves `null` for both fields on the company read, and the probe answers from
 * the surface that owns them.
 */
export function readAuthPolicySeed(company: unknown, probeBody: unknown): AuthPolicyState {
  return {
    tier: storedTier(company) ?? storedTier(probeBody) ?? DEFAULT_MFA_POLICY,
    graceDays: storedGraceDays(company) ?? storedGraceDays(probeBody) ?? DEFAULT_MFA_GRACE_DAYS,
  };
}

/**
 * Coerce a stored grace value into the endpoint's range.
 *
 * ⚠ `typeof x === 'number'` is not enough — `NaN` and `Infinity` are numbers, and both would survive
 * a naive clamp into the request body and 422. Non-integers are floored rather than rejected so a
 * value the backend somehow holds still renders as something the Owner can see and correct.
 */
export function readGraceDays(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_MFA_GRACE_DAYS;
  return clampGraceDays(Math.floor(value));
}

/** Clamp to `[MIN, MAX]`. The input control is bounded too; this is the value that actually ships. */
export function clampGraceDays(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_MFA_GRACE_DAYS;
  return Math.min(MAX_MFA_GRACE_DAYS, Math.max(MIN_MFA_GRACE_DAYS, Math.floor(value)));
}

/**
 * One employee's standing against the employer's policy (`EmployerMfaComplianceStatus`).
 *
 * `not_required` is NOT a euphemism for "unprotected" — it says the EMPLOYER asks nothing of this
 * person (the effective tier is below `require`). It carries no information about whether they have
 * a second factor, which is why no percentage is derived from it.
 */
export type MfaComplianceStatus = 'not_required' | 'enrolled' | 'pending' | 'overdue';

const MFA_STATUSES: readonly MfaComplianceStatus[] = [
  'not_required',
  'enrolled',
  'pending',
  'overdue',
];

/**
 * Read a roster row's `mfa_status`.
 *
 * `null` means NOT DISCLOSED, and three different things produce it — a Member admin (the backend
 * returns an empty map for anyone without `company:edit`, so every row reads `null`), a backend
 * predating AS-BE11 (the key is absent), and a value this build does not recognise. All three are
 * the same instruction to the UI: say nothing about this person.
 *
 * ★ Strict membership, not truthiness. A number, a boolean or an object arriving here is a shape
 * this build does not understand, and resolving it towards any of the four real states would put a
 * claim about someone's account security on screen on the strength of a decode accident.
 */
export function readMfaStatus(row: unknown): MfaComplianceStatus | null {
  const value = ((row ?? {}) as Record<string, unknown>).mfa_status;
  return typeof value === 'string' && (MFA_STATUSES as readonly string[]).includes(value)
    ? (value as MfaComplianceStatus)
    : null;
}

/**
 * AS-BE96 (D-AS-70) — whether this roster row's company sign-in is connected (`sso_linked`), which is
 * the whole gate on the Owner's "reset company sign-in" row action.
 *
 * The backend serves it to an Owner only, and only while company sign-in is on: a Member, a dark
 * environment and a backend that predates the field all read `null`, and every one of those must
 * draw no control. So only an explicit `true` counts — never truthiness, which would let a string
 * or an object offer a reset the server did not say was possible.
 *
 * Hand-decoded off the row because the committed snapshot does not declare the field yet
 * (`agent_docs/api-client.md` § *Additive RESPONSE fields the snapshot lacks*); the graduation pin is
 * in `security-decode.test.ts`.
 */
export function readSsoLinked(row: unknown): boolean {
  return ((row ?? {}) as Record<string, unknown>).sso_linked === true;
}

/**
 * AS-BE96 (review-80 F4) — whether the employer's sign-in lock binds THIS roster row's employee
 * (`sso_lock_active`), which decides the reset confirmation's sentence about the escape hatch.
 *
 * Per person, because it is true only of someone the lock binds: never a break-glass admin, who keeps
 * personal sign-in under the lock. Served beside `sso_linked` (Owner-only, dark with company sign-in),
 * so `true` and `false` are the server's answer and `null` is "not said", for anything else, which the
 * confirmation states conditionally rather than read as either.
 */
export function readSsoLockActive(row: unknown): boolean | null {
  const value = ((row ?? {}) as Record<string, unknown>).sso_lock_active;
  return value === true ? true : value === false ? false : null;
}

/** What the compliance panel renders. Every field is a count of rows THIS CLIENT ACTUALLY READ. */
export interface ComplianceSummary {
  /** Rows counted. The panel states this number rather than implying a company-wide figure. */
  counted: number;
  /**
   * A further page exists, so `counted` is a prefix of the roster and not the whole of it.
   *
   * ⚠ Detected with the fetch-one-extra probe, because the `{data,meta,links}` envelope's
   * `meta.total_items` is UNREACHABLE from the SDK — the response middleware unwraps the body to the
   * bare `data` array and discards every sibling (`agent_docs/api-client.md` § *Any top-level SIBLING
   * of `data` is unreachable*). There is no company-wide denominator available to this screen, so it
   * never renders one.
   */
  truncated: boolean;
  enrolled: number;
  pending: number;
  overdue: number;
  notRequired: number;
  /** Rows whose status this caller was not given (see {@link readMfaStatus}). */
  undisclosed: number;
  /**
   * At least one counted employee is being asked for a second factor — i.e. the deployment's
   * EFFECTIVE tier is `require`. The only enforcement fact a client can establish; see the header.
   */
  enforcementObserved: boolean;
  /**
   * Share of ASKED employees who have a server-verified factor, `0..1` — or `null` when nothing is
   * being asked, because then the wire carries no enrollment signal at all. Never defaults to 0.
   */
  enrolledShare: number | null;
}

/**
 * Count the statuses on a page of roster rows.
 *
 * `rows` is `unknown[]` on purpose: it is the tolerant-decoded list straight off the response, and
 * hand-modelling the row here would re-invite the generated-type mismatch this module exists to
 * avoid.
 */
export function summarizeCompliance(rows: readonly unknown[]): ComplianceSummary {
  let enrolled = 0;
  let pending = 0;
  let overdue = 0;
  let notRequired = 0;
  let undisclosed = 0;

  for (const row of rows) {
    switch (readMfaStatus(row)) {
      case 'enrolled':
        enrolled += 1;
        break;
      case 'pending':
        pending += 1;
        break;
      case 'overdue':
        overdue += 1;
        break;
      case 'not_required':
        notRequired += 1;
        break;
      default:
        undisclosed += 1;
    }
  }

  const asked = enrolled + pending + overdue;
  return {
    counted: rows.length,
    truncated: false,
    enrolled,
    pending,
    overdue,
    notRequired,
    undisclosed,
    enforcementObserved: asked > 0,
    enrolledShare: asked > 0 ? enrolled / asked : null,
  };
}

/** "63%" — only ever called with a real share; `null` renders no percentage at all. */
export function formatEnrolledShare(share: number): string {
  return `${Math.round(share * 100)}%`;
}

// ── The shared status vocabulary (AS-WEB4 panel + AS-WEB5 roster column) ────────────────────────
//
// The APPROVED status labels (FROZEN — Jonathan-signed 2026-08-12, byte-for-byte). One mapping used
// by BOTH surfaces, so a status cannot be called one thing in the Security tab and another on the
// Employees page — which is why they are frozen together and must change together.
// `security-copy-freeze.test.ts` pins each label exactly.
//
// `not_required` carries the constraint that needed sign-off: it is a fact about the POLICY (the
// employer asks nothing), never about the account. "Not asked" says that; "None", "Off" or
// "Unprotected" would each assert something about the person the server never said.

export interface MfaStatusPresentation {
  label: string;
  tone: 'success' | 'neutral' | 'danger';
}

// The words are `security-copy.ts`'s (`MFA_STATUS_LABELS`, AS-WEB8: every panel word in one place).
const STATUS_PRESENTATION: Record<MfaComplianceStatus, MfaStatusPresentation> = {
  enrolled: { label: MFA_STATUS_LABELS.enrolled, tone: 'success' },
  pending: { label: MFA_STATUS_LABELS.pending, tone: 'neutral' },
  overdue: { label: MFA_STATUS_LABELS.overdue, tone: 'danger' },
  // Not "None" or "Off": the fact is about the POLICY, not about this person's account.
  not_required: { label: MFA_STATUS_LABELS.not_required, tone: 'neutral' },
};

/**
 * One status's badge. `requirementLive` (AS-WEB20, D-AS-39 fix 4): under the company two-factor
 * requirement there is no grace period, so `overdue` reads "Not set up" (red, as ever); with the
 * requirement dark, the approved 2026-08-12 words, unchanged.
 */
export function mfaStatusPresentation(
  status: MfaComplianceStatus,
  requirementLive = false,
): MfaStatusPresentation {
  const presentation = STATUS_PRESENTATION[status];
  return requirementLive
    ? { ...presentation, label: MFA_STATUS_LABELS_UNDER_REQUIREMENT[status] }
    : presentation;
}

/**
 * Whether the roster should carry the two-factor column at all (AS-WEB5).
 *
 * TRUE exactly when some employee on the page is actually being asked for a second factor — the same
 * `enforcementObserved` fact the Security tab's panel branches on, deliberately shared so the two
 * screens cannot disagree about whether a policy is in play.
 *
 * Why not "show it whenever the field is present": below `require` every row reads `not_required`,
 * so the column would be a hundred identical badges carrying no information, on a page that is LIVE
 * on prod for every employer. And `null` (a Member, or a backend predating AS-BE11) must render
 * nothing at all rather than an empty column implying a status nobody has.
 */
export function rosterShowsMfaColumn(rows: readonly unknown[]): boolean {
  return rows.some((row) => {
    const status = readMfaStatus(row);
    return status !== null && status !== 'not_required';
  });
}
